import { useState, useEffect } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { Mail, ArrowLeft, Loader2, Clock } from 'lucide-react';
import { Button, Input, Card, CardHeader, CardTitle, CardDescription, CardContent } from '@nimbitlabs/ui';
import { PinInput } from './PinInput';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function MagicLinkLogin() {
  const navigate = useNavigate();
  const { requestPin: authRequestPin, login: authLogin, refreshSession } = useAuth();
  const [step, setStep] = useState<'email' | 'pin'>('email');
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [pinError, setPinError] = useState(false);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [loginSuccess, setLoginSuccess] = useState(false);

  // Countdown timer effect
  useEffect(() => {
    if (!expiresAt) return;

    const totalDuration = 5 * 60 * 1000; // 5 minutes in ms

    const updateTimer = () => {
      const now = new Date().getTime();
      const expiry = expiresAt.getTime();
      const diff = expiry - now;

      if (diff <= 0) {
        setTimeRemaining('expired');
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      const timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;
      const percentage = (diff / totalDuration) * 100;
      setTimeRemaining(`${timeString}|${percentage}`);
    };

    updateTimer(); // Initial update
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  const handleRequestPin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const result = await authRequestPin(email);
      
      if (result.success) {
        setStep('pin');
        if (result.expiresAt) {
          setExpiresAt(new Date(result.expiresAt));
        }
        toast.success('Check your email for the PIN code!', {
          description: 'The code will expire in 5 minutes',
        });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to send PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPin = async (pinCode: string) => {
    setLoading(true);
    setPinError(false);

    try {
      await authLogin(email, pinCode);
      
      toast.success('Welcome back!', {
        description: 'Successfully logged in',
      });

      // Set success state to trigger redirect
      setLoginSuccess(true);
    } catch (error) {
      setPinError(true);
      setPin('');
      toast.error(error instanceof Error ? error.message : 'Invalid PIN code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendPin = async () => {
    setPin('');
    setPinError(false);
    await handleRequestPin(new Event('submit') as any);
  };

  const handleBackToEmail = () => {
    setStep('email');
    setPin('');
    setPinError(false);
    setExpiresAt(null);
  };

  // Redirect to dashboard after successful login
  if (loginSuccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
      <Card className="w-full max-w-md border-border/50 shadow-xl">
        <CardHeader className="space-y-1 pb-6">
          {/* Back to Home Button */}
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="w-fit mb-4"
          >
            <Link to="/">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
          </Button>
          
          {/* Logo/Brand */}
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <Mail className="h-6 w-6 text-primary-foreground" />
            </div>
          </div>
          <CardTitle className="text-2xl text-center text-foreground">Welcome</CardTitle>
          <CardDescription className="text-center text-muted-foreground">
            {step === 'email' 
              ? 'Enter your email to get started' 
              : 'Enter the code we sent to your email'}
          </CardDescription>
        </CardHeader>

        {/* Main Content */}
        <CardContent>
          {step === 'email' ? (
            <form onSubmit={handleRequestPin} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  Email Address
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className="h-12 text-base bg-input border-border/50"
                  autoFocus
                  required
                />
              </div>

              <Button
                type="submit"
                variant="default"
                className="w-full h-12 text-base font-medium"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending Code...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Login Code
                  </>
                )}
              </Button>
            </form>
          ) : (
            <div className="space-y-6">
              {/* Email Display */}
              <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg border border-border/30">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-foreground">{email}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToEmail}
                  disabled={loading}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              </div>

              {/* PIN Input */}
              <div className="py-4">
                <PinInput
                  value={pin}
                  onChange={setPin}
                  onComplete={handleVerifyPin}
                  disabled={loading}
                  error={pinError}
                />
              </div>

              {/* Error Message */}
              {pinError && (
                <div className="text-center text-sm text-destructive">
                  Invalid code. Please try again.
                </div>
              )}

              {/* Countdown Timer with Circular Progress */}
              {expiresAt && timeRemaining && (
                <div className="flex justify-center">
                  {timeRemaining === 'expired' ? (
                    <div className="text-sm text-destructive font-medium">
                      Code expired. Please request a new one.
                    </div>
                  ) : (() => {
                    const [time, percentageStr] = timeRemaining.split('|');
                    const percentage = parseFloat(percentageStr);
                    const radius = 32;
                    const circumference = 2 * Math.PI * radius;
                    const strokeDashoffset = circumference - (percentage / 100) * circumference;
                    
                    // Color based on time remaining
                    const strokeColor = percentage > 50 
                      ? 'stroke-primary' 
                      : percentage > 20 
                        ? 'stroke-yellow-500' 
                        : 'stroke-destructive';

                    return (
                      <div className="relative inline-flex items-center justify-center">
                        {/* SVG Circular Progress */}
                        <svg className="w-20 h-20 -rotate-90" viewBox="0 0 72 72">
                          {/* Background circle */}
                          <circle
                            cx="36"
                            cy="36"
                            r={radius}
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                            className="text-border/30"
                          />
                          {/* Progress circle */}
                          <circle
                            cx="36"
                            cy="36"
                            r={radius}
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                            strokeDasharray={circumference}
                            strokeDashoffset={strokeDashoffset}
                            strokeLinecap="round"
                            className={`${strokeColor} transition-all duration-1000 ease-linear`}
                          />
                        </svg>
                        {/* Time display in center */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <Clock className="h-3 w-3 text-muted-foreground mb-0.5" />
                          <span className="text-sm font-bold text-foreground tabular-nums">
                            {time}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Resend Button */}
              <div className="text-center">
                <Button
                  variant="link"
                  onClick={handleResendPin}
                  disabled={loading}
                  className="text-sm text-primary hover:text-primary/80"
                >
                  Didn't receive the code? Resend
                </Button>
              </div>

              {/* Loading State */}
              {loading && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verifying...
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary hover:text-primary/80 font-medium">
                Sign up
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
